import {
  Children,
  cloneElement,
  isValidElement,
  useRef,
  useState,
  type ReactElement,
  type ReactNode,
  type Ref,
} from 'react'
import {
  FloatingArrow,
  FloatingPortal,
  arrow,
  autoUpdate,
  flip,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useMergeRefs,
  useRole,
  type Placement,
} from '@floating-ui/react'
import { cn } from '@chapche/ui'

export type TooltipVariant = 'ink' | 'beige'

export type TooltipProps = {
  content: ReactNode
  /** Optional bold line above content. */
  title?: ReactNode
  children: ReactElement
  placement?: Placement
  /** When false, hover never opens the tooltip (overflow / disabled gates). */
  enabled?: boolean
  variant?: TooltipVariant
  openDelayMs?: number
  className?: string
}

const VARIANT_PANEL: Record<TooltipVariant, string> = {
  ink: 'bg-ink text-white',
  beige: 'bg-light-beige text-ink-muted shadow-toggle',
}

const VARIANT_ARROW: Record<TooltipVariant, string> = {
  ink: 'fill-ink',
  beige: 'fill-light-beige',
}

type ChildProps = {
  ref?: Ref<unknown>
}

/**
 * Shared hover/focus tooltip (floating-ui + Tailwind).
 * Replaces HintItem / product__list-icon_hint absolute CSS hacks.
 */
export function Tooltip({
  content,
  title,
  children,
  placement = 'top',
  enabled = true,
  variant = 'ink',
  openDelayMs = 200,
  className,
}: TooltipProps) {
  const [open, setOpen] = useState(false)
  const arrowRef = useRef<SVGSVGElement>(null)
  const isOpen = enabled && open

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: (next) => {
      if (enabled) {
        setOpen(next)
      }
    },
    placement,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(8),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      arrow({ element: arrowRef }),
    ],
  })

  const hover = useHover(context, {
    enabled,
    move: false,
    delay: { open: openDelayMs, close: 0 },
  })
  const focus = useFocus(context, { enabled })
  const dismiss = useDismiss(context)
  const role = useRole(context, { role: 'tooltip' })
  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role,
  ])

  const child = Children.only(children)
  const childRef = isValidElement(child)
    ? (child.props as ChildProps).ref
    : null
  const mergedRef = useMergeRefs([refs.setReference, childRef])

  if (!isValidElement(child)) {
    return child
  }

  return (
    <>
      {cloneElement(child, {
        ...getReferenceProps(child.props as object),
        ref: mergedRef,
      } as Partial<typeof child.props> & { ref: typeof mergedRef })}
      <FloatingPortal>
        {isOpen ? (
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className={cn(
              'z-[200] w-max max-w-[300px] whitespace-pre-wrap break-words',
              'rounded-sm px-12 py-8 font-montserrat text-m leading-5',
              VARIANT_PANEL[variant],
              className,
            )}
            {...getFloatingProps()}
          >
            <FloatingArrow
              ref={arrowRef}
              context={context}
              width={10}
              height={6}
              className={VARIANT_ARROW[variant]}
            />
            {title ? (
              <p className="m-0 mb-4 font-montserrat-medium text-m">{title}</p>
            ) : null}
            {content}
          </div>
        ) : null}
      </FloatingPortal>
    </>
  )
}

export default Tooltip
